import React, { useEffect, useState } from 'react';
import clubService from '../../services/clubService';
import TintSquare from '../hand/TintSquare';

/**
 * CLOSED CLUBS — under the MY CLUBS grid: the clubs this person deleted that are still
 * inside the 30-day window, each with BRING IT BACK. Renders nothing when there are none,
 * so the page looks exactly as before for everyone else.
 */
export default function ClosedClubs({ onRestored, phone = false }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    // Older service mocks / bundles may not carry the call; then there is simply nothing to show.
    const p = typeof clubService.getMyDeletedClubs === 'function' ? clubService.getMyDeletedClubs() : null;
    if (p && typeof p.then === 'function') p.then((r) => { if (live && r?.success) setRows(r.data || []); });
    return () => { live = false; };
  }, []);

  if (rows.length === 0) return null;

  const restore = async (c) => {
    setBusy(c.id); setError(null);
    const r = await clubService.restoreClub(c.id);
    setBusy(null);
    if (!r.success) { setError(r.error || 'Could not bring it back. Try again.'); return; }
    setRows((rs) => rs.filter((x) => x.id !== c.id));
    onRestored?.(r.data);
  };

  return (
    <section style={{ background: '#fff', padding: phone ? '16px' : '22px 24px', display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="closed-clubs">
      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.2em' }}>CLOSED</span>
        <span style={{ fontSize: 10, letterSpacing: '0.12em', color: '#6B7280' }}>STILL YOURS TO BRING BACK FOR 30 DAYS</span>
      </span>
      {error && <span style={{ fontSize: 11, letterSpacing: '0.06em', color: '#DC2626' }} role="alert">{error}</span>}
      {rows.map((c) => {
        const daysLeft = Math.max(0, Math.ceil((new Date(c.restoreUntil).getTime() - Date.now()) / 86400000));
        return (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '2px solid #F2F2F2' }} data-testid="closed-club-row">
            <TintSquare name={c.name} imageUrl={c.avatarUrl} size={40} tintKey={c.id} style={{ borderRadius: 12, border: 'none' }} />
            <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
              <span style={{ fontSize: 10, letterSpacing: '0.1em', color: '#6B7280' }}>
                {c.memberCount} MEMBER{c.memberCount === 1 ? '' : 'S'} · GONE FOR GOOD IN {daysLeft} DAY{daysLeft === 1 ? '' : 'S'}
              </span>
            </span>
            <button type="button" onClick={() => restore(c)} disabled={busy === c.id || !c.canRestore}
              style={{ flexShrink: 0, height: 36, padding: '0 16px', border: 'none', borderRadius: 999, background: '#000', color: '#fff', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer', opacity: busy === c.id ? 0.6 : 1 }}
              data-testid="restore-club">
              {busy === c.id ? '…' : 'BRING IT BACK'}
            </button>
          </div>
        );
      })}
    </section>
  );
}
